import React from 'react';
import PropTypes from 'prop-types';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {
    isEmpty,
    extractWorkSheetData,
    getWithExpiry,
    formatDate,
    ExcelDateToDateString
} from "../../../globals/GlobalFunctions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import axios from 'axios';
import {refreshToken} from "../../../globals/GlobalFunctions";
import Autocomplete from "@mui/material/Autocomplete";
import {GlobalConstants} from "../../../globals/GlobalConstants";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

export interface Props {
    excelData: any;
    currentDB: string;
}

function BulkEDICodeMapImporter({excelData, currentDB}: Props) {
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [validationText, setValidationText] = React.useState("");
    const [validationError, setValidationError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [currentEDICodeMap, setCurrentEDICodeMap] = React.useState({direction: "",
        effective_Date: "", map_Type: "", map_Variable1: "", map_Variable2: "", vbasoftware_database: "CoastalAdminTest", vbapi_Token: "", map_Variable3: "",
        map_Variable4: "", map_Variable5: "", source_Value: "", term_Date: "", destination_Value: ""});

    let token = localStorage.getItem('jwt');

    const handleValidationClose = () => {
        setValidationError(false);
        setValidationText("");
    }

    const initialCodeMapState = {
        direction: "",
        effective_Date: "",
        map_Type: "",
        map_Variable1: "",
        map_Variable2: "",
        vbasoftware_database: "CoastalAdminTest",
        vbapi_Token: "",
        map_Variable3: "",
        map_Variable4: "",
        map_Variable5: "",
        source_Value: "",
        term_Date: "",
        destination_Value: ""
    }

    const clearEDICodeMaps = () => {
        setCurrentEDICodeMap({...initialCodeMapState});
    }

    const importEDICodeMaps = async (ediCodeMapArray : any[]) => {
        refreshToken();
        setIsLoading(true);
        const response =
            await axios
                .put(
                    localStorage.getItem('baseURL') + 'ediCodeMap/putList',
                    ediCodeMapArray,
                    {headers: {'Authorization': `Bearer ${token}`, accept: "*/*", "Content-Type": "application/json"}}
                ).then(() => {
                    setLoading(false);
                    setSuccessAlert(true);
                    setIsLoading(false);
                }).catch((error) => {
                    //console.log(response);
                    setLoading(false);
                    setValidationText(error.response.request.responseText);
                    setValidationError(true);
                    setIsLoading(false);
                });
        //console.log(response);
    }

    try {
        //Check for data and that there is a sheet in the data named IDCodeValue
        if (!isEmpty(excelData) && excelData.Sheets.EDICodeMap !== undefined) {
            //console.log(excelData, "Made it to formatter!");

            //global function to extract the data into a sequential array of cell data values as well as the column and row ranges
            let sheetDataArray = extractWorkSheetData(excelData.Sheets.EDICodeMap);

            let rawCellArray: any[] = sheetDataArray[0];

            let columnRange: number = sheetDataArray[1];

            let rowRange: number = sheetDataArray[2];

            let codeMapArray: any[] = [];

            //console.log(rawCellArray, "Raw Data");

            //we need to find the column index of the values we require so we can grab the data for each row -- THIS IS WHERE COLUMN NAMES ARE SET
            //we add 1 for each for the loop multiplier to account for it being zero based, but later we subtract 1 to account for the very fist row actually starting at zero
            const ediCodeMapIndexCollection = {
                directionIndex: rawCellArray.indexOf('Direction') + 1,
                mapTypeIndex: rawCellArray.indexOf('Map Type') + 1,
                sourceValueIndex: rawCellArray.indexOf('Source Value') + 1,
                destValueIndex: rawCellArray.indexOf('Destination Value') + 1,
                mapVar1Index: rawCellArray.indexOf('Map Variable 1') + 1,
                mapVar2Index: rawCellArray.indexOf('Map Variable 2') + 1,
                mapVar3Index: rawCellArray.indexOf('Map Variable 3') + 1,
                mapVar4Index: rawCellArray.indexOf('Map Variable 4') + 1,
                mapVar5Index: rawCellArray.indexOf('Map Variable 5') + 1,
                effDateIndex: rawCellArray.indexOf('Effective Date') + 1,
                termDateIndex: rawCellArray.indexOf('Term Date') + 1,
            }

            if(ediCodeMapIndexCollection.directionIndex > 0 && ediCodeMapIndexCollection.mapTypeIndex > 0 && ediCodeMapIndexCollection.sourceValueIndex > 0 &&
                ediCodeMapIndexCollection.destValueIndex > 0 && ediCodeMapIndexCollection.effDateIndex > 0) {

                for (let i = 1; i < rowRange; i++) {
                    //for each new array we skip over the previous records determined by the column length, removing 1 to account for our zero based index at the beginning
                    let rowMultiplier = columnRange * i - 1;

                    //start with direction
                    if (ediCodeMapIndexCollection.directionIndex > 0) {
                        currentEDICodeMap.direction = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.directionIndex)].toString().toUpperCase();
                    }

                    //map type
                    if (ediCodeMapIndexCollection.mapTypeIndex > 0) {
                        currentEDICodeMap.map_Type = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapTypeIndex)].toString();
                    }

                    //source value
                    if (ediCodeMapIndexCollection.sourceValueIndex > 0) {
                        currentEDICodeMap.source_Value = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.sourceValueIndex)].toString();
                    }

                    //dest value
                    if (ediCodeMapIndexCollection.destValueIndex > 0) {
                        currentEDICodeMap.destination_Value = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.destValueIndex)].toString().toUpperCase();
                    }

                    //map var 1
                    if (ediCodeMapIndexCollection.mapVar1Index > 0) {
                        currentEDICodeMap.map_Variable1 = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapVar1Index)].toString().toUpperCase();
                    }

                    //map var 2
                    if (ediCodeMapIndexCollection.mapVar2Index > 0) {
                        currentEDICodeMap.map_Variable2 = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapVar2Index)].toString().toUpperCase();
                    }

                    //map var 3
                    if (ediCodeMapIndexCollection.mapVar3Index > 0) {
                        currentEDICodeMap.map_Variable3 = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapVar3Index)].toString().toUpperCase();
                    }

                    //map var 4
                    if (ediCodeMapIndexCollection.mapVar4Index > 0) {
                        currentEDICodeMap.map_Variable4 = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapVar4Index)].toString().toUpperCase();
                    }

                    //map var 1
                    if (ediCodeMapIndexCollection.mapVar5Index > 0) {
                        currentEDICodeMap.map_Variable5 = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.mapVar5Index)].toString().toUpperCase();
                    }

                    //effective Date
                    if (ediCodeMapIndexCollection.effDateIndex > 0) {
                        let date = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.effDateIndex)].toString();
                        if (date.includes("/")) {
                            date = formatDate(date);
                        } else if (date.length > 0 && date.length < 6) {
                            date = ExcelDateToDateString(date);
                        }
                        if (date.includes("-")) {
                            date = date.substring(5, 7) + "/" + date.substring(8, 10) + "/" + date.substring(0, 4);
                        }
                        else if (date.length > 7) {
                            date = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
                        }
                        currentEDICodeMap.effective_Date = date.toString().toUpperCase();
                    }

                    //term Date
                    if (ediCodeMapIndexCollection.termDateIndex > 0) {
                        let date = rawCellArray[(rowMultiplier + ediCodeMapIndexCollection.termDateIndex)].toString();
                        if (date.includes("/")) {
                            date = formatDate(date);
                        } else if (date.length > 0 && date.length < 6) {
                            date = ExcelDateToDateString(date);
                        }
                        if (date.includes("-")) {
                            date = date.substring(5, 7) + "/" + date.substring(8, 10) + "/" + date.substring(0, 4);
                        }
                        else if (date.length > 7) {
                            date = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
                        }
                        currentEDICodeMap.term_Date = date.toString().toUpperCase();
                    }

                    currentEDICodeMap.vbasoftware_database = currentDB;

                    currentEDICodeMap.vbapi_Token = getWithExpiry('vbapi_Token');

                    if((currentEDICodeMap.direction.length > 0 || currentEDICodeMap.map_Type.length > 0) && currentEDICodeMap.source_Value.length > 0
                        && currentEDICodeMap.destination_Value.length > 0){
                        codeMapArray.push({...currentEDICodeMap});
                    }
                    else {
                        // setValidationText("Group ID, Plan ID, and Vendor are required.");
                        // setValidationError(true);
                    }

                    currentEDICodeMap.direction = initialCodeMapState.direction;
                    currentEDICodeMap.effective_Date = initialCodeMapState.effective_Date;
                    currentEDICodeMap.map_Type = initialCodeMapState.map_Type;
                    currentEDICodeMap.map_Variable1 = initialCodeMapState.map_Variable1;
                    currentEDICodeMap.map_Variable2 = initialCodeMapState.map_Variable2;
                    currentEDICodeMap.map_Variable3 = initialCodeMapState.map_Variable3;
                    currentEDICodeMap.map_Variable4 = initialCodeMapState.map_Variable4;
                    currentEDICodeMap.map_Variable5 = initialCodeMapState.map_Variable5;
                    currentEDICodeMap.source_Value = initialCodeMapState.source_Value;
                    currentEDICodeMap.term_Date = initialCodeMapState.term_Date;
                    currentEDICodeMap.destination_Value = initialCodeMapState.destination_Value;

                    clearEDICodeMaps();
                }


                importEDICodeMaps(codeMapArray);

                excelData.Sheets.EDICodeMap = undefined;
            }
            else {
                //setValidationText("Group ID, Plan ID, and Vendor are required.");
                //setValidationError(true);
            }

        }
    }
    catch(e) {
        console.log("ERROR", e);

        setErrorAlert(true);
        //clear it out after errors in case of rerendering otherwise the error spams will continue until morale improves
        excelData.Sheets.EDICodeMap = undefined;
    }

    return (
        <div>
            {successAlert ? <Alert className="groupAlertStyles" severity='success' onClose={() => setSuccessAlert(false)} variant="filled">
                <AlertTitle>Success!</AlertTitle>
                Code Map Import Complete!
            </Alert> : <></> }
            {errorAlert ? <Alert className="groupAlertStyles" severity='error' onClose={() => setErrorAlert(false)} variant="filled">
                <AlertTitle>Error!</AlertTitle>
                There was an error with this file!
            </Alert> : <></> }
            <Dialog
                open={validationError}
                onClose={handleValidationClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Validation Error"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {validationText}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleValidationClose}>OK</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
}

export default React.memo(BulkEDICodeMapImporter)

BulkEDICodeMapImporter.propTypes = {
    excelData: PropTypes.object.isRequired
};
